import { useEffect, useState, useCallback, useRef } from "react";
import { UploadCloud, Trash2, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const SECTIONS = [
  { value: "portfolio", label: "Portfolio (main gallery)" },
  { value: "home", label: "Home - Recent Work" },
  { value: "process", label: "Process - Shop / CAD" },
  { value: "about", label: "About - Team / Shop" },
];
const SECTION_LABEL = Object.fromEntries(SECTIONS.map((s) => [s.value, s.label]));

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [section, setSection] = useState("portfolio");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gallery");
      setImages(data);
    } catch {
      toast.error("Could not load gallery.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("section", section);
    setUploading(true);
    try {
      const { data } = await api.post("/gallery/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.uploaded} photo${data.uploaded === 1 ? "" : "s"} to ${SECTION_LABEL[section]}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setImages((prev) => prev.filter((i) => i.id !== id));
      toast.success("Photo removed");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const grouped = SECTIONS.map((s) => ({ ...s, items: images.filter((i) => (i.section || "portfolio") === s.value) }));

  return (
    <div className="bg-white border border-navy/10 mt-6 p-6" data-testid="gallery-manager">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl text-navy">Photo Manager</h2>
          <p className="text-navy/50 text-sm">Upload finished-work photos to any page section. They appear instantly on the site.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="w-full sm:w-64">
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger data-testid="gallery-section-select" className="rounded-none border-navy/20"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {SECTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <input ref={inputRef} type="file" accept="image/*" multiple hidden data-testid="gallery-file-input" onChange={(e) => onFiles(e.target.files)} />
          <button data-testid="gallery-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="flex items-center justify-center gap-2 bg-navy text-ivory tracking-wider uppercase text-xs px-6 py-3 hover:bg-navy-surface transition-colors disabled:opacity-60 whitespace-nowrap">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {uploading ? "Uploading" : "Upload Photos"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-gold animate-spin" /></div>
      ) : (
        <div className="mt-8 space-y-8">
          {grouped.map((g) => (
            <div key={g.value} data-testid={`gallery-group-${g.value}`}>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-navy font-medium text-sm tracking-wider uppercase">{g.label}</h3>
                <span className="text-navy/40 text-xs">({g.items.length})</span>
                <span className="h-px flex-1 bg-navy/10" />
              </div>
              {g.items.length === 0 ? (
                <div className="flex items-center gap-2 text-navy/30 text-sm py-4"><ImageOff className="h-4 w-4" /> No photos in this section yet.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {g.items.map((img) => (
                    <div key={img.id} className="group relative aspect-square overflow-hidden bg-navy/5" data-testid={`gallery-manage-${img.id}`}>
                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => remove(img.id)} data-testid={`gallery-delete-${img.id}`} title="Delete"
                        className="absolute top-1.5 right-1.5 bg-red-600/90 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
